/**
 * The household step's draft, and the ONE rule for seeding it from the
 * server's proposal (F35 · D-17).
 *
 * The server decides WHAT to propose (the saved roster, else the onboarding
 * answer, else nobody — `GET …/household-proposal`). This decides how a
 * proposal meets a draft the citizen may already have touched, which is where
 * the board's trap lives: "a saved widget draft must win over the seed". Kept
 * out of the component so the rule is a function with a spec, not an effect.
 */
import type {
  HouseholdProposal,
  HouseholdProposalMember,
  HouseholdProposalSource,
  ProposedHouseholdMemberType,
} from '@tonnie/types';

/** One person (or pet) as the step edits them. */
export interface HouseholdDraftMember {
  /**
   * The member's STABLE ref. Sent to the server as their identity, and what a
   * per-member task code is built from. It used to be a browser-lifetime
   * counter thrown away on submit, so the server fell back to the member's
   * NAME — which meant a rename minted a new task and left the passport
   * already uploaded on the archived one, and two children called Maria shared
   * a single task. A ref that came back from the server is reused verbatim.
   */
  id: string;
  type: ProposedHouseholdMemberType;
  name: string;
  nationality?: string;
  /** Unset until the citizen ticks or unticks it — "not answered" is saved as NULL. */
  hasPassport?: boolean;
  /** Unset until the citizen ticks or unticks it — "not answered" is saved as NULL. */
  vaccinationOk?: boolean;
  species?: string;
}

/**
 * A type alias rather than an interface: `useWidgetState` persists a
 * `Record<string, unknown>`, which an interface (no implicit index signature)
 * does not satisfy.
 */
export type HouseholdDraft = {
  hasPartner: boolean;
  hasChildren: boolean;
  hasPets: boolean;
  partner: HouseholdDraftMember;
  children: HouseholdDraftMember[];
  pets: HouseholdDraftMember[];
  /**
   * Where the prefill came from, once applied. `null` until then. Persisted
   * with the draft, so the prefill is applied ONCE per draft: a citizen who
   * edited what was proposed and came back finds their edits, not a fresh
   * proposal on top of them.
   */
  prefillSource: HouseholdProposalSource | null;
};

/** The species a new pet row starts with — the pet select's first option. */
export const DEFAULT_PET_SPECIES = 'dog';

/**
 * The draft after a proposal arrives, or `null` when the proposal must not
 * touch it (it was already seeded once).
 *
 * The toggles only ever turn ON from a proposal, and a partner, child list or
 * pet list the citizen already filled in is kept as it is. A proposed member
 * has no identity yet; one is minted by `mintId`, exactly as for a member the
 * citizen adds by hand, and saved when they confirm.
 */
export function seedHouseholdDraft(
  draft: HouseholdDraft,
  proposal: HouseholdProposal,
  mintId: (type: ProposedHouseholdMemberType) => string
): HouseholdDraft | null {
  if (draft.prefillSource !== null) return null;

  const toDraft = (m: HouseholdProposalMember): HouseholdDraftMember => ({
    id: m.clientRef ?? mintId(m.type),
    type: m.type,
    name: m.name,
    nationality: m.nationality ?? '',
    hasPassport: m.hasPassport ?? undefined,
    vaccinationOk: m.vaccinationOk ?? undefined,
    species: m.type === 'pet' ? (m.petSpecies ?? DEFAULT_PET_SPECIES) : undefined,
  });
  const proposedPartner = proposal.members.find((m) => m.type === 'partner');
  const proposedChildren = proposal.members.filter((m) => m.type === 'child').map(toDraft);
  const proposedPets = proposal.members.filter((m) => m.type === 'pet').map(toDraft);

  return {
    prefillSource: proposal.source,
    hasPartner: draft.hasPartner || Boolean(proposedPartner),
    hasChildren: draft.hasChildren || proposedChildren.length > 0,
    hasPets: draft.hasPets || proposedPets.length > 0,
    partner:
      draft.partner.name.trim() || !proposedPartner ? draft.partner : toDraft(proposedPartner),
    children: draft.children.length > 0 ? draft.children : proposedChildren,
    pets: draft.pets.length > 0 ? draft.pets : proposedPets,
  };
}
